import { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import { getAuthState, navigateToDashboard, setAuthState } from "shared-web"

export default function LoginCallback() {
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading')
  const [error, setError] = useState<string>('')

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Check if we have auth data in the URL or local storage
        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')
        const state = urlParams.get('state')
        const email = urlParams.get('email')

        // Check for existing auth state first
        let authState = getAuthState()

        if (code || email || authState) {
          // Handle OAuth callback or existing auth
          if (!authState && (code || email)) {
            // This is a real OAuth callback from Iris
            // The backend should have already set auth cookies
            // We just need to check if the auth was successful
            throw new Error('OAuth callback handling not yet implemented - use /login for dev auth')
          }

          if (authState) {
            setStatus('success')

            // Clean up URL parameters
            const url = new URL(window.location.href)
            url.searchParams.delete('code')
            url.searchParams.delete('state')
            url.searchParams.delete('email')
            window.history.replaceState({}, document.title, url.toString())

            // Redirect to dashboard after success state is shown
            setTimeout(() => {
              navigateToDashboard()
            }, 1500)
          } else {
            throw new Error('Authentication state not found')
          }
        } else {
          // No auth data found
          setError('No authentication data received. Please try logging in again.')
          setStatus('error')
        }
      } catch (err) {
        console.error('Auth callback error:', err)
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed'
        setError(errorMessage)
        setStatus('error')
      }
    }

    // Add a small delay to show loading state
    const timer = setTimeout(handleAuthCallback, 500)
    return () => clearTimeout(timer)
  }, [])

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-700 via-green-600 to-lime-500">
        <div className="text-center text-white">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Completing sign in...</h2>
          <p className="text-green-100">Please wait while we set up your account.</p>
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-700 via-red-600 to-red-500">
        <div className="text-center text-white max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-semibold mb-2">Sign in failed</h2>
          <p className="text-red-100 mb-6">{error}</p>
          <a
            href="/login"
            className="inline-block bg-white text-red-600 px-6 py-3 rounded-lg font-medium hover:bg-red-50 transition-colors"
          >
            Try again
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-700 via-green-600 to-lime-500">
      <div className="text-center text-white">
        <div className="text-6xl mb-4">✅</div>
        <h2 className="text-2xl font-semibold mb-2">Sign in successful!</h2>
        <p className="text-green-100">Redirecting to your dashboard...</p>
      </div>
    </div>
  )
}